import { createSlice } from '@reduxjs/toolkit';
import api from '../util/api';
import logger from '../util/logger';
import { authenticationSlice } from './authentication';
import moment from 'moment';
import _ from 'lodash';
import draftEntryHelper from 'helpers/draft-entry-helper';
import { DeviceIcon } from 'data/common/common';
import { reject } from 'lodash';
import { listDraftFromServer } from './app';
import { resolve } from 'superagent/lib/request-base';
import enums from 'util/enums';

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    rawData: [],
    signing: [],
    draft: [],
    notaryProfile: [],
    documentType: [],
    signingPackage: [],
    location: [],
    journalExport: [],
    device: []
  },
  reducers: {
    cleanupState: (state, action) => {
      state.rawData = [];
      state.draft = [];
      state.notaryProfile = [];
      state.documentType = [];
      state.signingPackage = [];
      state.location = [];
      state.journalExport = [];
      state.device = [];
    },

    setRawData: (state, action) => {
      state.rawData = action.payload.data;
    },
    setEntriesDraft: (state, action) => {
      state.draft = action.payload.data;
    },
    setEntriesNotaryProfile: (state, action) => {
      state.notaryProfile = action.payload.data;
    },
    setEntriesDocumentType: (state, action) => {
      state.documentType = action.payload.data;
    },
    setEntriesSigningPackage: (state, action) => {
      state.signingPackage = action.payload.data;
    },
    setEntriesLocation: (state, action) => {
      state.location = action.payload.data;
    },
    setEntriesJournalExport: (state, action) => {
      state.journalExport = action.payload.data;
    },
    setEntriesDevice: (state, action) => {
      state.device = action.payload.data;
    }
  }
});

let {
  setRawData,
  setEntriesDraft,
  setEntriesNotaryProfile,
  setEntriesDocumentType,
  setEntriesSigningPackage,
  setEntriesLocation,
  setEntriesJournalExport,
  setEntriesDevice,
  setLoadError,
  cleanupState
} = dashboardSlice.actions;

const getEnumerationDateFilter = (Start, End, allTime = false) => {
  if (allTime) {
    return {};
  }

  const startDate =
    Start || moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
  const endDate = End
    ? moment(End).endOf('day').format('YYYY-MM-DD HH:mm:ss')
    : moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');

  return {
    CreatedAfterUtc: moment(startDate).utc().format(),
    CreatedBeforeUtc: moment(endDate).utc().format()
  };
};

let actions = {
  actionSetRawData: (Start, End, Fidelity, allTime = false) => {
    return async (dispatch, getState) => {
      try {
        // Reports require a date window and max out at 256 slices. All Time uses
        // Discrete fidelity (summary only, no slices) with a wide window.
        let StartDate = allTime
          ? '2000-01-01'
          : Start ||
            moment().subtract(7, 'days').endOf('day').format('YYYY-MM-DD');
        let EndDate = allTime
          ? moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
          : End
          ? moment(End).endOf('day').format('YYYY-MM-DD HH:mm:ss')
          : moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        let FidelityInterval = allTime ? 'Discrete' : Fidelity || 'Daily';
        const res = await api.Reports.signing(
          StartDate,
          EndDate,
          FidelityInterval
        );
        if (res?.Success === false) {
          logger.log('actionSetRawData report failed', res);
          dispatch(setRawData({ data: {} }));
          return false;
        }
        dispatch(setRawData({ data: res || {} }));
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setRawData({ data: {} }));
        return false;
      }
    };
  },

  actionGetDashboardStats: (Start, End, Fidelity) => {
    return async (dispatch, getState) => {
      try {
        let StartDate =
          Start || moment().subtract(1, 'months').format('YYYY-MM-DD');
        let EndDate = End
          ? moment(End).endOf('day').format('YYYY-MM-DD HH:mm:ss')
          : moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        let FidelityInterval = Fidelity || 'Daily';
        return await new Promise((resolve, reject) => {
          api.Reports.signing(StartDate, EndDate, FidelityInterval).then(
            res => {
              resolve(res);
            }
          );
        });
      } catch (err) {
        reject(false);
      }
    };
  },

  actionSetEntriesDraft: (Start, End, allTime = false) => {
    logger.log('actionSetEntriesDraft calling');
    return async (dispatch, getState) => {
      let userMaster = _.get(getState(), 'app.userMaster');
      if (!userMaster) {
        return false;
      }

      try {
        const res = await dispatch(
          listDraftFromServer({
            IncludeData: true,
            returnMeta: true,
            MaxResults: 1000,
            ...getEnumerationDateFilter(Start, End, allTime)
          })
        );
        dispatch(setEntriesDraft({ data: res }));
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setEntriesDraft({ data: [] }));
        return false;
      }
    };
  },

  actionSetEntriesNotaryProfile: () => {
    return (dispatch, getState) => {
      let rawList = _.get(getState(), 'app.userProfile');
      let itemData = [];
      if (rawList) {
        _.map(rawList, item => {
          itemData.push({
            name: item.CommissionedName,
            expirationDate:
              moment(item.ExpirationDate).format('MM/DD/YYYY') || null
          });
        });
        dispatch(setEntriesNotaryProfile({ data: itemData }));
      }
    };
  },

  actionSetEntriesDocumentType: (Start, End, allTime = false) => {
    return async (dispatch, getState) => {
      try {
        const res = await api.Enumeration(enums.ENUMERATION_TYPES.DOCTYPE, {
          MaxResults: 1000,
          ...getEnumerationDateFilter(Start, End, allTime)
        });
        const list = res?.Objects || [];
        let rawList = _.sortBy(list, d => {
          return -moment(d.Created).unix();
        });
        let itemData = [];
        if (rawList) {
          _.map(rawList, item => {
            itemData.push({
              name: item.Value,
              created: moment(item.Created).format('MM/DD/YYYY') || null
            });
          });
          dispatch(setEntriesDocumentType({ data: itemData }));
        }
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setEntriesDocumentType({ data: [] }));
        return false;
      }
    };
  },

  actionSetEntriesSigningPackage: (Start, End, allTime = false) => {
    return async (dispatch, getState) => {
      try {
        const res = await api.Enumeration(enums.ENUMERATION_TYPES.DOCCOLL, {
          MaxResults: 1000,
          ...getEnumerationDateFilter(Start, End, allTime)
        });
        let rawList = _.sortBy(res?.Objects || [], d => {
          return d.Name;
        });

        let itemData = [];
        if (rawList) {
          _.map(rawList, item => {
            itemData.push({
              name: item.Name,
              created: moment(item.Created).format('MM/DD/YYYY') || null
            });
          });
          dispatch(setEntriesSigningPackage({ data: itemData }));
        }
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setEntriesSigningPackage({ data: [] }));
        return false;
      }
    };
  },

  actionSetEntriesLocation: (Start, End, allTime = false) => {
    return async (dispatch, getState) => {
      try {
        const res = await api.Enumeration(enums.ENUMERATION_TYPES.LOCATION, {
          MaxResults: 1000,
          ...getEnumerationDateFilter(Start, End, allTime)
        });
        let rawList = _.sortBy(res?.Objects || [], d => {
          return -moment(d.Created).unix();
        });

        let itemData = [];
        if (rawList) {
          _.map(rawList, item => {
            itemData.push({
              name: item.DisplayName,
              created: moment(item.Created).format('MM/DD/YYYY') || null
            });
          });
          dispatch(setEntriesLocation({ data: itemData }));
        }
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setEntriesLocation({ data: [] }));
        return false;
      }
    };
  },

  actionSetEntriesJournalExport: () => {
    return (dispatch, getState) => {
      let now = moment().utc();
      let userMaster = _.get(getState(), 'app.userMaster');
      if (userMaster) {
        let userCreatedMonth = moment.utc(userMaster.Created).startOf('month');
        let displayDateRanges = [];
        let curMonthEnd = moment(now);
        let curMonthStart = moment(now).startOf('month');
        while (curMonthStart.isSameOrAfter(userCreatedMonth, 'month')) {
          displayDateRanges.push({
            displayKey: displayDateRanges.length + 1,
            begin: curMonthStart.toString(),
            end: curMonthEnd.toString(),
            month: moment(curMonthStart).format('MMMM'),
            viewDate: moment(curMonthStart).format('YYYY-MM-DD')
          });

          curMonthEnd = moment(curMonthEnd).subtract(1, 'month').endOf('month');
          curMonthStart = moment(curMonthStart)
            .subtract(1, 'month')
            .startOf('month');
        }

        dispatch(setEntriesJournalExport({ data: displayDateRanges }));
      }
    };
  },
  actionSetEntriesDevice: (Start, End, allTime = false) => {
    return async (dispatch, getState) => {
      try {
        const res = await api.Enumeration(enums.ENUMERATION_TYPES.DEVICE, {
          MaxResults: 1000,
          ...getEnumerationDateFilter(Start, End, allTime)
        });
        let rawList = _.sortBy(res?.Objects || [], d => {
          return -moment(d.LastSeen).unix();
        });

        let itemData = [];
        if (rawList && rawList.length > 0) {
          _.map(rawList, item => {
            itemData.push({
              name: `${item.Manufacturer} ${item.Model} `,
              created: moment(item.Created).format('MM/DD/YYYY') || null,
              Lastseen: moment(item.LastSeen).format('MM/DD/YYYY') || null,
              blocked: item.Blocked,
              icon: DeviceIcon(item, true)
            });
          });
          dispatch(setEntriesDevice({ data: itemData }));
        } else {
          dispatch(setEntriesDevice({ data: [] }));
        }
        return true;
      } catch (err) {
        logger.log(err);
        dispatch(setEntriesDevice({ data: [] }));
        return false;
      }
    };
  },

  refreshDashboardData: (Start, End, Fidelity, allTime = false) => {
    return async (dispatch, getState) => {
      await Promise.allSettled([
        dispatch(actions.actionSetRawData(Start, End, Fidelity, allTime)),
        dispatch(actions.actionSetEntriesDraft(Start, End, allTime)),
        dispatch(actions.actionSetEntriesDocumentType(Start, End, allTime)),
        dispatch(actions.actionSetEntriesSigningPackage(Start, End, allTime)),
        dispatch(actions.actionSetEntriesLocation(Start, End, allTime)),
        dispatch(actions.actionSetEntriesDevice(Start, End, allTime))
      ]);
      dispatch(actions.actionSetEntriesNotaryProfile());
      dispatch(actions.actionSetEntriesJournalExport());
      return true;
    };
  },

  // refreshDashboardData: () => {
  //   return async (dispatch, getState) => {
  //     const res = await new Promise((resolve, reject) => {
  //       dispatch(actions.actionSetRawData(null, null, null));
  //     }).then(res => {
  //       return Promise.all([
  //         dispatch(actions.actionSetEntriesDraft()),
  //         dispatch(actions.actionSetEntriesNotaryProfile()),
  //         dispatch(actions.actionSetEntriesDocumentType()),
  //         dispatch(actions.actionSetEntriesSigningPackage()),
  //         dispatch(actions.actionSetEntriesLocation()),
  //         dispatch(actions.actionSetEntriesJournalExport()),
  //         dispatch(actions.actionSetEntriesDevice())
  //       ]).then(res => {
  //         resolve(res);
  //       })
  //     }).catch(err => {
  //       reject(false);
  //     })
  //   };
  // },

  dashboardCleanup: () => {
    return (dispatch, getState) => {
      dispatch(cleanupState());
    };
  }
};

export const {
  actionSetRawData,
  actionSetEntriesDraft,
  actionSetEntriesNotaryProfile,
  actionSetEntriesDocumentType,
  actionSetEntriesSigningPackage,
  actionSetEntriesLocation,
  actionSetEntriesJournalExport,
  refreshDashboardData,
  dashboardCleanup,
  actionGetDashboardStats
} = actions;

export default dashboardSlice.reducer;
